/**
 * First URL segments that lead somewhere in this app: every top-level route folder under
 * `src/app` (including folders inside route groups such as `(home)`) and every folder under
 * `public/`. Signed-out visitors to a path under one of these still get sent to sign-in; any
 * other page path that isn't public gets the not-found page with a real 404 from the proxy
 * (`src/middleware/not-found.ts`), so mistyped or stale links don't land on a login page and
 * crawlers don't see soft 404s.
 *
 * `app-route-segments.test.ts` walks `src/app` and `public/` and fails when this list and the
 * filesystem disagree, so a new top-level route has to be added here.
 *
 * `api` folders are left out: API requests keep their 401 and never consult this list.
 */
export const APP_TOP_LEVEL_SEGMENTS = [
  // src/app
  '.well-known',
  '404',
  'action',
  'admin',
  'agents',
  'ai-code-review',
  'best-ai-code-review-tools',
  'bugbench',
  'bugbench.md',
  'cli',
  'demo',
  'desktop',
  'docs',
  'error',
  'framer',
  'indexnow-key.txt',
  'info',
  'inside-ai-coding',
  'llms.txt',
  'paul',
  'pr',
  'pricing',
  'privacy',
  'settings',
  'standards',
  'super-admin',
  'survey',
  'terms',
  'v1',
  'vs',
  'wikis',
  'wrapped',
  // src/app/(auth)
  'auth',
  'onboarding',
  'sign-in',
  'sign-up',
  'vercel',
  // src/app/(home)
  'agents-library',
  'ai-review',
  'analytics',
  'code',
  'codebase-scan',
  'dashboard',
  'inbox',
  'wiki',
  // src/app/(homepage)
  'download',
  // public/
  'buttons',
  'fonts',
  'schema',
  'slack',
  'sounds',
  'templates',
] as const;

const KNOWN_SEGMENTS: ReadonlySet<string> = new Set(APP_TOP_LEVEL_SEGMENTS);

const FRAMEWORK_SEGMENT_PATTERN = /^(?:_next|_vercel|__nextjs)/;

// A top-level dynamic segment (`[slug]`, `[...slug]`) would match any first segment.
const HAS_TOP_LEVEL_DYNAMIC_SEGMENT = APP_TOP_LEVEL_SEGMENTS.some((segment) =>
  segment.startsWith('['),
);

/**
 * Decodes one URL path segment the way the App Router matches it, or returns null when the
 * segment is not valid percent-encoding.
 */
export function decodePathSegment(segment: string): string | null {
  try {
    return decodeURIComponent(segment);
  } catch {
    return null;
  }
}

/**
 * Whether the first segment of `pathname` belongs to a route that exists in the app. Only the
 * first segment is checked, so `/inbox/anything` counts as an app route.
 */
export function isAppRoutePath(pathname: string): boolean {
  if (HAS_TOP_LEVEL_DYNAMIC_SEGMENT) {
    return true;
  }

  const rawFirstSegment = pathname.split('/')[1] ?? '';
  // A segment that isn't valid percent-encoding is matched as written.
  const firstSegment = decodePathSegment(rawFirstSegment) ?? rawFirstSegment;

  // The root is always routed, and framework and platform paths (`/_next`, `/_vercel`, the dev
  // overlay's `/__nextjs_*`) never belong to an app folder; all keep their existing handling.
  if (firstSegment === '' || FRAMEWORK_SEGMENT_PATTERN.test(firstSegment)) {
    return true;
  }

  return KNOWN_SEGMENTS.has(firstSegment);
}
